import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'

interface TooltipProps {
  content: ReactNode
  children: ReactNode
  className?: string
  side?: 'top' | 'bottom'
  disabled?: boolean
}

interface TipPos {
  x: number
  y: number
  side: 'top' | 'bottom'
}

export function Tooltip({ content, children, className = '', side = 'top', disabled = false }: TooltipProps) {
  const reduced = useReducedMotion()
  const anchorRef = useRef<HTMLSpanElement>(null)
  const holdTimer = useRef(0)
  const showTimer = useRef(0)
  const [pos, setPos] = useState<TipPos | null>(null)

  const show = () => {
    const el = anchorRef.current
    if (!el || disabled) return
    const r = el.getBoundingClientRect()
    const vw = window.innerWidth
    const wantTop = side === 'top' && r.top > 72
    const cx = Math.min(Math.max(r.left + r.width / 2, 96), vw - 96)
    setPos({
      x: cx,
      y: wantTop ? r.top - 10 : r.bottom + 10,
      side: wantTop ? 'top' : 'bottom',
    })
  }

  const hide = () => {
    window.clearTimeout(holdTimer.current)
    window.clearTimeout(showTimer.current)
    setPos(null)
  }

  useEffect(() => {
    if (!pos) return
    const onScroll = () => hide()
    window.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => window.removeEventListener('scroll', onScroll, { capture: true })
  }, [pos])

  useEffect(() => () => hide(), [])

  return (
    <span
      ref={anchorRef}
      className={['inline-flex', className].join(' ')}
      onPointerEnter={(e) => {
        if (e.pointerType === 'touch') return
        window.clearTimeout(showTimer.current)
        showTimer.current = window.setTimeout(show, 240)
      }}
      onPointerLeave={hide}
      onPointerDown={(e) => {
        if (e.pointerType !== 'touch') return
        window.clearTimeout(holdTimer.current)
        holdTimer.current = window.setTimeout(show, 320)
      }}
      onPointerUp={() => window.clearTimeout(holdTimer.current)}
      onPointerCancel={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {createPortal(
        <AnimatePresence>
          {pos && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              className="pointer-events-none fixed"
              style={{
                left: pos.x,
                top: pos.y,
                transform: `translate(-50%, ${pos.side === 'top' ? '-100%' : '0'})`,
                zIndex: 'var(--z-toast)' as unknown as number,
              }}
            >
              <motion.div
                role="tooltip"
                initial={reduced ? undefined : { scale: 0.94, y: pos.side === 'top' ? 4 : -4 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 550, damping: 32 }}
                className="max-w-[248px] rounded-[var(--r-sm)] border border-[var(--edge)]/60 bg-[var(--surface-raised)] px-3 py-2 text-xs leading-snug text-[var(--text)] shadow-raised"
              >
                {content}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </span>
  )
}

export function TooltipBody({ title, lines }: { title: string; lines: (string | null | undefined)[] }) {
  return (
    <span className="flex flex-col gap-0.5">
      <span className="font-display font-bold text-[var(--ember)]">{title}</span>
      {lines.filter(Boolean).map((l) => (
        <span key={l as string} className="text-[var(--text-dim)]">
          {l}
        </span>
      ))}
    </span>
  )
}
